import { useEffect, useRef } from 'react';

import { cn } from '@/lib/utils';

export interface ToastDrainBarProps {
  durationMs: number;
  resetKey: number | string;
  paused: boolean;
  /** When omitted, the bar is visual-only (Sonner owns dismiss). */
  onElapsed?: () => void;
  testId?: string;
  className?: string;
}

/**
 * Two-pixel drain along the bottom edge of a toast card. Pauses when `paused` is true.
 */
export function ToastDrainBar({
  durationMs,
  resetKey,
  paused,
  onElapsed,
  testId = 'toast-drain-bar',
  className,
}: ToastDrainBarProps) {
  const barRef = useRef<HTMLDivElement>(null);
  const pausedRef = useRef(paused);
  const onElapsedRef = useRef(onElapsed);
  pausedRef.current = paused;
  onElapsedRef.current = onElapsed;

  useEffect(() => {
    let remaining = durationMs;
    let last = performance.now();
    let frame = 0;
    const tick = (now: number) => {
      const delta = now - last;
      last = now;
      if (!pausedRef.current) {
        remaining -= delta;
        if (barRef.current) {
          barRef.current.style.transform = `scaleX(${Math.max(0, remaining / durationMs)})`;
        }
        if (remaining <= 0) {
          onElapsedRef.current?.();
          return;
        }
      }
      frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [durationMs, resetKey]);

  return (
    <div
      className={cn(
        'absolute inset-x-0 bottom-0 h-0.5 bg-foreground/10 motion-reduce:hidden',
        className,
      )}
      data-testid={testId}
      aria-hidden
    >
      <div
        ref={barRef}
        className="h-full w-full origin-left bg-foreground/55 motion-reduce:hidden"
      />
    </div>
  );
}
